import { type ThunkAction } from "redux-thunk";
import { type RootState } from "../../redux/store";
import { type AnyAction } from "../../redux/types";
import { sendData as addToComparisonCall } from "../api/AddToComparison";
import { sendData as removeFromComparisonCall } from "../api/RemoveFromComparison";
import { sendData as removeAllFromComparison } from "../api/RemoveAllFromComparison";
import { sendData as addToComparisonBulkCall } from "../api/AddToComparisonBulk";
import { trackAddToComparisonClick } from "~/ProductComparison/analytics";
import { type ProductData } from "~/models/ProductData";
import { type PdpPageProductDataDto } from "~/ProductDetails/dto/PdpPageProductDataDto";
import { transformPdpProduct } from "~/ProductDetails/transformers/PdpPageProductDataTransformer";
import { addBaseProductDataAction } from "~/redux/products/actions";

import { isNonNull } from "~/utils/reducers";

export const POPULATE_INITIAL_COMPARISON_LIST = "ProductComparison: Populate initial list";
export const REMOVE_FROM_COMPARISON_LIST_SUCCESS = "ProductComparison: Remove item success";
export const REMOVE_FROM_COMPARISON_LIST_FAIL = "ProductComparison: Remove item fail";
export const ADD_TO_COMPARISON_LIST_SUCCESS = "ProductComparison: Add item success";
export const ADD_TO_COMPARISON_LIST_FAIL = "ProductComparison: Add item fail";
export const REMOVE_ALL_FROM_COMPARISON_SUCCESS = "ProductComparison: Remove all success";
export const REMOVE_ALL_FROM_COMPARISON_FAIL = "ProductComparison: Remove all fail";
export const COMPARISON_LIST_FULL = "ProductComparison: List full";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function populateInitialComparisonList(data: string[]): ThunkResult<void> {
  return async (dispatch) => {
    return dispatch({
      type: POPULATE_INITIAL_COMPARISON_LIST,
      payload: data,
    });
  };
}

export function addToComparison(productId: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState().productComparison;
    const { comparisonList } = state;
    const exists = comparisonList.some((id) => id === productId);

    if (exists) {
      const apiResponse = await removeFromComparisonCall(productId);
      if (apiResponse.status === 200) {
        return dispatch({
          type: REMOVE_FROM_COMPARISON_LIST_SUCCESS,
          payload: productId,
        });
      }
      return dispatch({
        type: REMOVE_FROM_COMPARISON_LIST_FAIL,
        payload: productId,
      });
    } else {
      const apiResponse = await addToComparisonCall(productId);
      if (apiResponse.status === 200) {
        return dispatch({
          type: ADD_TO_COMPARISON_LIST_SUCCESS,
          payload: productId,
        });
      }
      if (apiResponse.status === 400) {
        return dispatch({
          type: COMPARISON_LIST_FULL,
          payload: productId,
        });
      }
      return dispatch({
        type: ADD_TO_COMPARISON_LIST_FAIL,
        payload: productId,
      });
    }
  };
}
export function addToComparisonBulk(data: string[]): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState().productComparison;
    const { comparisonList } = state;

    const productCodesToAdd = data.filter((item) => {
      return !comparisonList.includes(item);
    });

    if (!productCodesToAdd) {
      return;
    }

    const apiResponse = await addToComparisonBulkCall(productCodesToAdd);
    if (apiResponse) {
      if (apiResponse.status === 200) {
        const dtos: PdpPageProductDataDto[] = await apiResponse.json();
        const productData = productCodesToAdd
          .map((code) => {
            const dto = dtos.find((dto) => dto.code === code);
            return dto ? transformPdpProduct(dto, null) : null;
          })
          .filter(isNonNull);
        dispatch(addBaseProductDataAction(productData, true));
        productCodesToAdd.map((item) => {
          return dispatch({
            type: ADD_TO_COMPARISON_LIST_SUCCESS,
            payload: item,
          });
        });
      }
      if (apiResponse.status === 400) {
        productCodesToAdd.map((item) => {
          return dispatch({
            type: COMPARISON_LIST_FULL,
            payload: item,
          });
        });
      }
      return dispatch({
        type: ADD_TO_COMPARISON_LIST_FAIL,
      });
    }
  };
}

export function addProductsToComparison(products: ProductData[]) {
  return {
    type: ADD_TO_COMPARISON_LIST_SUCCESS,
    payload: products,
  };
}

export function removeFromComparison(productCode: string, productDesignation: string): ThunkResult<void> {
  return async (dispatch) => {
    const apiResponse = await removeFromComparisonCall(productCode);
    if (apiResponse.status === 200) {
      trackAddToComparisonClick(productDesignation, false, "Product Comparison");
      return dispatch({
        type: REMOVE_FROM_COMPARISON_LIST_SUCCESS,
        payload: productCode,
      });
    }
    return dispatch({
      type: REMOVE_FROM_COMPARISON_LIST_FAIL,
      payload: productCode,
    });
  };
}

export function clearComparisonList(): ThunkResult<void> {
  return async (dispatch) => {
    const apiResponse = await removeAllFromComparison();
    if (apiResponse.status === 200) {
      return dispatch({
        type: REMOVE_ALL_FROM_COMPARISON_SUCCESS,
      });
    }
    return dispatch({
      type: REMOVE_ALL_FROM_COMPARISON_FAIL,
    });
  };
}

export type ProductComparisonActions =
  | ReturnType<typeof addToComparison>
  | ReturnType<typeof removeFromComparison>
  | ReturnType<typeof clearComparisonList>;
